import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from "@angular/core";
import { CoreLogService } from "../../../core/services/log.service";

@Component({
  selector: "sot-numberPad",
  templateUrl: "./numberPad.component.html",
  styleUrls: ["./numberPad.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class NumberPad {

  public constructor(private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService) {
  }

  @Input() requiredLength: number;
  @Input() displayDots: boolean;
  @Input() validateFirstChar: boolean;
  @Input() hideInput: boolean;
  @Input() isPIN: boolean;
  /** Min input length to enable submit. Null → must match requiredLength exactly. */
  @Input() minLength: number = null;
  /** Max input length; further digits rejected. Null → capped at requiredLength. */
  @Input() maxLength: number = null;
  /** Toggles the slot border-right separators. */
  @Input() showSeparators: boolean = true;

  public numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
  public numberPadResult: string = "";
  public inputMade: boolean = false;
  public inputValid: boolean = true;
  public animationStates: { [key: number]: string } = {};
  public removeBtnState: string = "";

  public ngOnInit(): void { }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("NumberPad.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public get inputNumbers(): string[] {
    const me = this;
    const result = [];
    let inputString = me.numberPadResult;
    if (me.hideInput && me.inputMade) {
      if (me.isPIN || inputString.length === me.requiredLength) {
        inputString = inputString.replace(/./g, '*');
      } else {
        inputString = inputString.slice(0, -1).replace(/./g, '*') + inputString.slice(-1);
      }
    }

    for (let i = 0; i < me.requiredLength; i++) {
      result.push(inputString.substring(i, i + 1));
    }

    return result;
  }

  /**
   * Adds a tapped number to the input.
   * @param {number} tappedNumber - The number to add.
   */
  public addNumber(tappedNumber: number): void {
    const me = this;
    const cap = me.maxLength !== null ? me.maxLength : me.requiredLength;
    if (me.numberPadResult.length < cap) {
      me.animateOnAdd(tappedNumber);
      me.numberPadResult = me.numberPadResult + tappedNumber;
      me.inputMade = true;
      me.PushChanges();
    }
  }

  /**
   * Removes the last number from the input.
   */
  public removeLast(): void {
    const me = this;
    if (me.numberPadResult.length > 0) {
      me.numberPadResult = me.numberPadResult.substring(0, me.numberPadResult.length - 1);
      me.animateOnRemove();
      me.PushChanges();
    }
  }

  /**
   * Toggles the animation state for the given number.
   * @param {number} number - The number to animate.
   */
  private animateOnAdd(number: number) {
    const me = this;
    me.animationStates[number] = me.animationStates[number] === 'start' ? 'end' : 'start';
  }

  /** 
   * Toggles the animation state
   */
  private animateOnRemove() {
    const me = this;
    me.removeBtnState = me.removeBtnState === 'start' ? 'end' : 'start';
  }

  public toggleInputValid() {
    const me = this;
    me.inputValid = !me.inputValid;
    me.PushChanges();
  }

  public get validateLength(): boolean {
    const me = this;
    return me.numberPadResult.length === me.requiredLength;
  }

  /** True when the input has reached its capacity (maxLength when set, otherwise requiredLength). */
  public get atMaxLength(): boolean {
    const me = this;
    const cap = me.maxLength !== null ? me.maxLength : me.requiredLength;
    return me.numberPadResult.length >= cap;
  }

  public get firstCharIsValid(): boolean {
    const me = this;
    return me.numberPadResult.length > 0 ? me.numberPadResult.charAt(0) !== '0' : true;
  }

  /** minLength gate takes precedence when set; validateFirstChar is not evaluated in that case. */
  public get submitDisabled() {
    const me = this;
    if (me.minLength !== null) {
      return me.numberPadResult.length < me.minLength;
    }
    if (me.validateFirstChar) {
      return !me.firstCharIsValid || !me.validateLength;
    } else {
      return !me.validateLength;
    }
  }

}
